import { useQuery } from "@tanstack/react-query";

import { dataflowApi } from "@/services/api/dataflow";
import { useDataFlowStore } from "@/stores/dataflowStore";
import { TaskTypeBadge } from "@/components/dataflow/TaskTypeBadge";

function eventColor(type: string): string {
  if (type.endsWith("completed")) return "text-emerald-300";
  if (type.endsWith("failed")) return "text-rose-300";
  if (type.endsWith("cancelled")) return "text-orange-300";
  return "text-sky-300";
}

function fmtTime(raw: string): string {
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

export function TimelineRuntimeView() {
  const { setSelectedInstance } = useDataFlowStore();

  const { data, isLoading } = useQuery({
    queryKey: ["dataflow", "timeline"],
    queryFn: () => dataflowApi.getTimeline({ limit: 200 }),
    refetchInterval: 10000,
  });

  if (isLoading) {
    return <div className="p-4 text-sm text-[var(--muted)]">Loading timeline...</div>;
  }

  const events = data?.events || [];

  return (
    <div className="h-full overflow-auto p-3">
      <div className="rounded border border-[var(--border)] bg-[var(--panel)]">
        <div className="grid grid-cols-[180px_170px_110px_1fr] gap-3 border-b border-[var(--border)] px-3 py-2 text-[11px] text-[var(--muted)]">
          <span>Timestamp</span>
          <span>Event</span>
          <span>Instance</span>
          <span>Message</span>
        </div>
        <div className="divide-y divide-[var(--border)]">
          {events.map((event) => (
            <button
              key={event.id}
              type="button"
              onClick={() => setSelectedInstance(event.instance_id)}
              className="grid w-full grid-cols-[180px_170px_110px_1fr] gap-3 px-3 py-2 text-left text-xs hover:bg-white/[0.03]"
            >
              <span className="text-[var(--muted)]">{fmtTime(event.timestamp)}</span>
              <span className={eventColor(event.type)}>{event.type}</span>
              <span className="text-slate-200">#{event.instance_id}</span>
              <span className="flex items-center gap-2 truncate">
                {event.data?.task_type ? (
                  <TaskTypeBadge taskType={String(event.data.task_type)} />
                ) : (
                  <span className="text-slate-300">{String(event.data?.name || "")}</span>
                )}
                {event.data?.error_msg ? (
                  <span className="truncate text-rose-300">· {String(event.data.error_msg)}</span>
                ) : null}
              </span>
            </button>
          ))}
          {events.length === 0 && (
            <div className="px-3 py-3 text-xs text-[var(--muted)]">No events yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
